/**
 * Standings utilities for fantasy baseball leagues.
 * Supports three scoring types:
 *
 * - Head-to-Head (Categories or Points): Standings = W-L-T from weekly matchups.
 *   Each category win = 1 win (H2H Categories); or each week winner = 1 W (H2H Points).
 *
 * - Rotisserie (Roto): Teams ranked 1..N in each category; 1st = N points, last = 1.
 *   Total roto points = sum of category rank points. Standings sorted by total (desc).
 *
 * - Total Points (Points-based, season long): No matchups. Standings = total season
 *   points per team. Sorted by total points (desc).
 */

const DEFAULT_H2H_CATEGORIES = [
  { key: 'R', label: 'R', higherWins: true },
  { key: 'HR', label: 'HR', higherWins: true },
  { key: 'RBI', label: 'RBI', higherWins: true },
  { key: 'SB', label: 'SB', higherWins: true },
  { key: 'AVG', label: 'AVG', higherWins: true },
  { key: 'W', label: 'W', higherWins: true },
  { key: 'SV', label: 'SV', higherWins: true },
  { key: 'K', label: 'K', higherWins: true },
  { key: 'ERA', label: 'ERA', higherWins: false },
  { key: 'WHIP', label: 'WHIP', higherWins: false },
];

/**
 * Normalize league teams to array.
 */
function getTeams(league) {
  const raw = league?.teams;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return [];
    }
  }
  return [];
}

/**
 * Get scoring type and flags from league settings.
 */
export function getScoringTypeInfo(league) {
  const scoringType = league?.settings?.scoringType || 'Rotisserie';
  const isHeadToHead =
    scoringType.includes('Head-to-Head') ||
    scoringType === 'Head-to-Head Each Category' ||
    scoringType === 'Head-to-Head Most Categories' ||
    scoringType === 'Head-to-Head Points';
  const isRotisserie =
    scoringType === 'Rotisserie' || scoringType === 'Rotisserie Regular Season';
  const isPointsBased =
    scoringType === 'Points-based' || scoringType === 'Head-to-Head Points';
  return { scoringType, isHeadToHead, isRotisserie, isPointsBased };
}

/**
 * Get categories for H2H from league settings or default 5x5.
 */
function getH2HCategories(league) {
  const batting = league?.settings?.battingCategories;
  const pitching = league?.settings?.pitchingCategories;
  if (Array.isArray(batting) && Array.isArray(pitching)) {
    const battingCats = batting.map((c) => ({
      key: c.key || c,
      label: c.label || c.key || c,
      higherWins: !(c.isAverage || (c.key && ['AVG', 'OBP', 'SLG', 'OPS'].includes(c.key))),
    }));
    const pitchingCats = pitching.map((c) => ({
      key: c.key || c,
      label: c.label || c.key || c,
      higherWins: !(c.key && ['ERA', 'WHIP'].includes(c.key)),
    }));
    return [...battingCats, ...pitchingCats];
  }
  return DEFAULT_H2H_CATEGORIES;
}

/**
 * Build matchups from league (stored or generated). Returns list of
 * { team1Id, team2Id, week, team1FullWins, team2FullWins, totalCategories }.
 * Uses deterministic mock stats when real stats are not available.
 */
export function buildMatchupsWithResults(league) {
  const teams = getTeams(league);
  const storedMatchups = league?.settings?.schedule?.matchups || [];
  const categories = getH2HCategories(league);
  const totalCategories = categories.length;
  const getTeamById = (id) => teams.find((t) => t.id === id || t.teamId === id);

  if (storedMatchups.length > 0) {
    return storedMatchups
      .filter((m) => getTeamById(m.team1Id) && getTeamById(m.team2Id))
      .map((m) => {
        const week = m.week || m.period || 1;
        const t1 = getTeamById(m.team1Id);
        const t2 = getTeamById(m.team2Id);
        const i1 = teams.findIndex((t) => t.id === t1?.id || t.teamId === t1?.teamId);
        const i2 = teams.findIndex((t) => t.id === t2?.id || t.teamId === t2?.teamId);
        const seed = (i1 + 1) * 7 + (i2 + 1) * 11 + week * 13;
        const team1Wins = Math.abs((seed % totalCategories) - Math.floor(totalCategories / 2)) + 1;
        const team2Wins = totalCategories - team1Wins;
        return {
          team1Id: m.team1Id,
          team2Id: m.team2Id,
          week,
          team1FullWins: Math.max(0, Math.min(team1Wins, totalCategories)),
          team2FullWins: Math.max(0, Math.min(team2Wins, totalCategories)),
          totalCategories,
        };
      });
  }

  const matchups = [];
  const totalWeeks = 18;
  for (let week = 1; week <= totalWeeks; week++) {
    for (let i = 0; i < teams.length; i += 2) {
      if (i + 1 < teams.length) {
        const seed = (i + 1) * 7 + (i + 2) * 11 + week * 13;
        const team1Wins = Math.abs((seed % totalCategories) - Math.floor(totalCategories / 2)) + 1;
        const team2Wins = totalCategories - team1Wins;
        matchups.push({
          team1Id: teams[i].id || teams[i].teamId,
          team2Id: teams[i + 1].id || teams[i + 1].teamId,
          week,
          team1FullWins: Math.max(0, Math.min(team1Wins, totalCategories)),
          team2FullWins: Math.max(0, Math.min(team2Wins, totalCategories)),
          totalCategories,
        });
      }
    }
  }
  return matchups;
}

/**
 * Compute Head-to-Head standings (W-L-T) from matchups.
 */
function computeHeadToHeadStandings(league) {
  const teams = getTeams(league);
  const matchups = buildMatchupsWithResults(league);
  const records = {};
  teams.forEach((t) => {
    const id = t.id || t.teamId;
    records[id] = { wins: 0, losses: 0, ties: 0 };
  });

  matchups.forEach((m) => {
    const id1 = m.team1Id;
    const id2 = m.team2Id;
    const w1 = m.team1FullWins ?? 0;
    const w2 = m.team2FullWins ?? 0;
    const ties = (m.totalCategories ?? 0) - w1 - w2;
    if (records[id1]) {
      records[id1].wins += w1;
      records[id1].losses += w2;
      records[id1].ties += ties;
    }
    if (records[id2]) {
      records[id2].wins += w2;
      records[id2].losses += w1;
      records[id2].ties += ties;
    }
  });

  return teams.map((team) => {
    const id = team.id || team.teamId;
    const r = records[id] || { wins: 0, losses: 0, ties: 0 };
    return {
      ...team,
      wins: r.wins,
      losses: r.losses,
      ties: r.ties,
    };
  });
}

/**
 * Get category keys for Rotisserie from league settings or default 5x5.
 */
function getRotoCategoryKeys(league) {
  const batting = league?.settings?.battingCategories;
  const pitching = league?.settings?.pitchingCategories;
  if (Array.isArray(batting) && Array.isArray(pitching)) {
    return [
      ...batting.map((c) => ({ key: c.key || c, higherIsBetter: true })),
      ...pitching.map((c) => ({ key: c.key || c, higherIsBetter: !['ERA', 'WHIP'].includes(c.key) })),
    ];
  }
  return [
    { key: 'R', higherIsBetter: true },
    { key: 'HR', higherIsBetter: true },
    { key: 'RBI', higherIsBetter: true },
    { key: 'SB', higherIsBetter: true },
    { key: 'AVG', higherIsBetter: true },
    { key: 'W', higherIsBetter: true },
    { key: 'SV', higherIsBetter: true },
    { key: 'K', higherIsBetter: true },
    { key: 'ERA', higherIsBetter: false },
    { key: 'WHIP', higherIsBetter: false },
  ];
}

/**
 * Compute Rotisserie standings: rank 1..N in each category (1st = N points), sort by total.
 */
function computeRotisserieStandings(league) {
  const teams = getTeams(league);
  const categoryConfig = getRotoCategoryKeys(league);
  const n = teams.length;

  const categoryTotals = {};
  categoryConfig.forEach(({ key }) => {
    categoryTotals[key] = {};
  });

  teams.forEach((team, index) => {
    const id = team.id || team.teamId;
    categoryConfig.forEach(({ key, higherIsBetter }) => {
      const base = (n - index) * 10;
      const noise = (id?.length ?? 0) % 7;
      if (key === 'AVG' || key === 'OBP' || key === 'SLG' || key === 'OPS') {
        categoryTotals[key][id] = (0.2 + (index / n) * 0.1 + noise * 0.001).toFixed(3);
      } else if (key === 'ERA' || key === 'WHIP') {
        categoryTotals[key][id] = (4.5 - (index / n) * 1.5 + noise * 0.01).toFixed(2);
      } else {
        categoryTotals[key][id] = Math.max(0, base + noise + index);
      }
    });
  });

  const rotoPoints = {};
  teams.forEach((t) => {
    rotoPoints[t.id || t.teamId] = 0;
  });

  categoryConfig.forEach(({ key, higherIsBetter }) => {
    const values = teams.map((t) => ({
      id: t.id || t.teamId,
      value: parseFloat(categoryTotals[key][t.id || t.teamId]) ?? 0,
    }));
    values.sort((a, b) => (higherIsBetter ? b.value - a.value : a.value - b.value));
    values.forEach((v, rank) => {
      const points = n - rank;
      rotoPoints[v.id] = (rotoPoints[v.id] || 0) + points;
    });
  });

  return teams.map((team) => {
    const id = team.id || team.teamId;
    const categoryValues = {};
    categoryConfig.forEach(({ key }) => {
      categoryValues[key] = categoryTotals[key][id];
    });
    return {
      ...team,
      rotoPoints: rotoPoints[id] ?? 0,
      categoryValues,
      wins: 0,
      losses: 0,
      ties: 0,
    };
  });
}

/**
 * Compute Total Points (season long) standings. Sort by total points desc.
 */
function computeTotalPointsStandings(league) {
  const teams = getTeams(league);
  const n = teams.length;
  return teams.map((team, index) => {
    const id = team.id || team.teamId;
    const basePoints = 500 + (n - index) * 80;
    const noise = (id?.length ?? 0) % 50;
    return {
      ...team,
      points: Math.max(0, basePoints + noise),
      wins: 0,
      losses: 0,
      ties: 0,
    };
  });
}

/**
 * Compute standings for a league based on its scoring type.
 * Returns { scoringType, isHeadToHead, isRotisserie, isPointsBased, standings, categories }.
 */
export function computeStandings(league) {
  const teams = getTeams(league);
  if (!teams.length) {
    return {
      scoringType: 'Rotisserie',
      isHeadToHead: false,
      isRotisserie: true,
      isPointsBased: false,
      standings: [],
      categories: [],
    };
  }

  const typeInfo = getScoringTypeInfo(league);
  let standings;

  if (typeInfo.isHeadToHead) {
    standings = computeHeadToHeadStandings(league);
    standings.sort((a, b) => {
      if (b.wins !== a.wins) return b.wins - a.wins;
      if (a.losses !== b.losses) return a.losses - b.losses;
      return (b.ties || 0) - (a.ties || 0);
    });
    return {
      ...typeInfo,
      standings,
      categories: getH2HCategories(league),
    };
  }

  if (typeInfo.isRotisserie) {
    standings = computeRotisserieStandings(league);
    standings.sort((a, b) => (b.rotoPoints ?? 0) - (a.rotoPoints ?? 0));
    return {
      ...typeInfo,
      standings,
      categories: getRotoCategoryKeys(league).map((c) => ({ key: c.key, label: c.key, higherIsBetter: c.higherIsBetter })),
    };
  }

  if (typeInfo.isPointsBased) {
    standings = computeTotalPointsStandings(league);
    standings.sort((a, b) => (b.points ?? 0) - (a.points ?? 0));
    return {
      ...typeInfo,
      standings,
      categories: [],
    };
  }

  standings = computeRotisserieStandings(league);
  standings.sort((a, b) => (b.rotoPoints ?? 0) - (a.rotoPoints ?? 0));
  return {
    ...typeInfo,
    standings,
    categories: getRotoCategoryKeys(league).map((c) => ({ key: c.key, label: c.key, higherIsBetter: c.higherIsBetter })),
  };
}
